import {
  faDownload,
  faEye,
  faFile,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Loading, Modal } from "@wfp/ui"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import useCommon from "hooks/useCommon"
import moment from "moment"
import { useRef, useState } from "react"
import { useParams } from "react-router-dom"
import {
  CONTRACT_TYPE_LABELS,
  ContractType,
  ContractAttachment,
  useDeleteContractAttachmentMutation,
  useDownloadContractAttachmentMutation,
  useGetContractQuery,
  useListContractAttachmentsQuery,
  useListCountriesQuery,
  useUploadContractAttachmentMutation,
} from "services/aimsBackend"
import ContractFormModal from "./AddContractPage"

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024
const ALLOWED_ATTACHMENT_EXTENSIONS = [".bmp", ".gif", ".jpeg", ".jpg", ".pdf", ".png", ".webp", ".xls", ".xlsx"]

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col sm:flex-row mb-2" style={{ gap: 8, fontSize: 14, minWidth: 0 }}>
      <p className="m-0 sm:min-w-[140px]" style={{ fontWeight: 700 }}>
        {label}
      </p>
      <p className="m-0" style={{ overflowWrap: "anywhere" }}>
        {value || "-"}
      </p>
    </div>
  )
}

function formatDate(iso?: string): string {
  if (!iso) return "-"
  const m = moment(iso)
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : "-"
}

function isImageAttachment(attachment: ContractAttachment): boolean {
  return (
    attachment.content_type.startsWith("image/") ||
    [".bmp", ".gif", ".jpeg", ".jpg", ".png", ".webp"].some(extension =>
      attachment.name.toLowerCase().endsWith(extension),
    )
  )
}

function attachmentIcon(attachment: ContractAttachment) {
  const lowerName = attachment.name.toLowerCase()
  if (isImageAttachment(attachment)) return faFileImage
  if (attachment.content_type === "application/pdf" || lowerName.endsWith(".pdf")) return faFilePdf
  if (lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx")) return faFileExcel
  return faFile
}

function formatFileSize(size: number): string {
  return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(size / 1024)} KB`
}

function errorMessage(reason: any): string {
  const data = reason?.data
  if (typeof data === "string") return data
  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([field, detail]) => `${field}: ${Array.isArray(detail) ? detail.join(", ") : String(detail)}`)
      .join("; ")
  }
  return "Attachment operation failed"
}

export default function ContractDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const contractId = id ? Number(id) : NaN
  const { showSnackBar } = useCommon()
  const [showEditModal, setShowEditModal] = useState(false)
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ContractAttachment | null>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  const {
    data: contract,
    isLoading,
    error,
  } = useGetContractQuery(contractId, {
    skip: Number.isNaN(contractId),
  })
  const { data: countries = [] } = useListCountriesQuery()
  const { data: attachments = [], isFetching: isFetchingAttachments } = useListContractAttachmentsQuery(contractId, {
    skip: Number.isNaN(contractId),
  })
  const [uploadAttachment, { isLoading: isUploadingAttachment }] = useUploadContractAttachmentMutation()
  const [downloadAttachment] = useDownloadContractAttachmentMutation()
  const [deleteAttachment] = useDeleteContractAttachmentMutation()
  const countryNameByIso3 = new Map(countries.map(c => [c.iso3, c.name]))

  const getAttachmentBlob = async (attachment: ContractAttachment) => {
    try {
      return await downloadAttachment(attachment.id).unwrap()
    } catch (reason) {
      showSnackBar(errorMessage(reason), "error")
      return null
    }
  }

  const handleDownload = async (attachment: ContractAttachment) => {
    const blob = await getAttachmentBlob(attachment)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = attachment.name
    link.click()
    URL.revokeObjectURL(url)
  }

  const handlePreview = async (attachment: ContractAttachment) => {
    const blob = await getAttachmentBlob(attachment)
    if (!blob) return
    setPreview({ name: attachment.name, url: URL.createObjectURL(blob) })
  }

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url)
    setPreview(null)
  }

  const handleDeleteAttachment = async () => {
    if (!deleteTarget) return
    try {
      await deleteAttachment(deleteTarget.id).unwrap()
      showSnackBar("Attachment removed", "success")
    } catch (reason) {
      showSnackBar(errorMessage(reason), "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files) return
    const selectedFiles = Array.from(files)
    const invalidFiles = selectedFiles.filter(file => {
      const extension = `.${file.name.split(".").pop()?.toLowerCase()}`
      return file.size >= MAX_ATTACHMENT_SIZE || !ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)
    })
    const validFiles = selectedFiles.filter(file => !invalidFiles.includes(file))
    let uploadedCount = 0

    await validFiles.reduce(
      (previous, file) =>
        previous.then(async () => {
          try {
            await uploadAttachment({ contractId, file }).unwrap()
            uploadedCount += 1
          } catch (reason) {
            showSnackBar(`${file.name}: ${errorMessage(reason)}`, "error")
          }
        }),
      Promise.resolve(),
    )
    if (uploadedCount > 0) {
      showSnackBar(`${uploadedCount} attachment${uploadedCount === 1 ? "" : "s"} uploaded`, "success")
    }
    if (invalidFiles.length > 0) {
      showSnackBar(
        `${invalidFiles
          .map(file => file.name)
          .join(", ")}: only images, PDF, XLS, or XLSX files under 10 MB are allowed`,
        "error",
      )
    }
    if (attachmentInputRef.current) attachmentInputRef.current.value = ""
  }

  if (isLoading) {
    return (
      <div className="mt-4">
        <Loading active />
      </div>
    )
  }

  if (error || !contract) {
    return <p>Contract not found</p>
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-2">
      <div className="bg-white rounded-md p-4" style={{ minWidth: 0 }}>
        <div className="mb-1">
          <h2 className="m-0">Contract Details</h2>
          <p className="m-0 mt-1" style={{ fontSize: 13, color: "#5a6872" }}>
            CNT-{String(contract.id).padStart(5, "0")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <div>
            <InfoRow label="Name" value={contract.name} />
            <InfoRow label="Owner" value={contract.owner} />
            <InfoRow label="Countries" value={contract.countries.map(c => countryNameByIso3.get(c) ?? c).join(", ")} />
          </div>
          <div>
            <InfoRow
              label="Contract Types"
              value={contract.contract_types.map(t => CONTRACT_TYPE_LABELS[t as ContractType] ?? t).join(", ")}
            />
            <InfoRow label="Subscription Year" value={String(contract.subscription_year)} />
          </div>
        </div>

        {contract.description && (
          <div className="mb-3">
            <p className="m-0 mb-1" style={{ fontWeight: 700, fontSize: 14 }}>
              Description
            </p>
            <p className="m-0" style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
              {contract.description}
            </p>
          </div>
        )}

        {contract.rounds_covered && Object.keys(contract.rounds_covered).length > 0 && (
          <div className="mb-3">
            <p className="m-0 mb-1" style={{ fontWeight: 700, fontSize: 14 }}>
              Rounds Covered
            </p>
            <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2" style={{ fontWeight: 600, width: 200 }}>
                    Type
                  </th>
                  <th className="text-left p-2" style={{ fontWeight: 600 }}>
                    Rounds
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(contract.rounds_covered).map(([type, rounds]) => (
                  <tr key={type} style={{ borderBottom: "1px solid #e8e8e8" }}>
                    <td className="p-2">{CONTRACT_TYPE_LABELS[type as ContractType] ?? type}</td>
                    <td className="p-2">{rounds.length ? rounds.join(", ") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <div>
            <InfoRow label="Created" value={formatDate(contract.created_at)} />
          </div>
          <div>
            <InfoRow label="Last Updated" value={formatDate(contract.updated_at)} />
          </div>
        </div>

        <div className="flex flex-row items-center justify-end w-full">
          <Button kind="primary" onClick={() => setShowEditModal(true)}>
            Edit Contract
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-md p-4" style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <h2 className="m-0">Attachments</h2>
          <Button
            kind="accent"
            small
            disabled={isUploadingAttachment}
            onClick={() => attachmentInputRef.current?.click()}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            {isUploadingAttachment ? "Uploading..." : "Add attachments"}
          </Button>
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            accept=".bmp,.gif,.jpeg,.jpg,.pdf,.png,.webp,.xls,.xlsx"
            style={{ display: "none" }}
            onChange={event => handleFilesSelected(event.target.files)}
          />
        </div>

        <div style={{ border: "1px solid #d8d8d8", borderRadius: 6, overflow: "hidden", width: "100%" }}>
          {isFetchingAttachments && <Loading active />}
          {!isFetchingAttachments && attachments.length === 0 && (
            <p className="m-0 p-3" style={{ color: "#5a6872", fontSize: 13 }}>
              No attachments uploaded.
            </p>
          )}
          {attachments.map(attachment => (
            <div
              key={attachment.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 8,
                padding: "8px 12px",
                borderBottom: "1px solid #d8d8d8",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <FontAwesomeIcon icon={attachmentIcon(attachment)} style={{ color: "#007dbc", fontSize: 20 }} />
                <div style={{ minWidth: 0 }}>
                  <p className="m-0" style={{ fontWeight: 500, fontSize: 14, overflowWrap: "anywhere" }}>
                    {attachment.name}
                  </p>
                  <p className="m-0" style={{ color: "#5a6872", fontSize: 12 }}>
                    {formatFileSize(attachment.size)} · {formatDate(attachment.created_at)}
                  </p>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 4,
                  flexShrink: 0,
                  marginLeft: "auto",
                }}
              >
                {isImageAttachment(attachment) && (
                  <Button
                    kind="secondary"
                    small
                    title="Preview attachment"
                    aria-label="Preview attachment"
                    onClick={() => handlePreview(attachment)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                )}
                <Button
                  kind="secondary"
                  small
                  title="Download attachment"
                  aria-label="Download attachment"
                  onClick={() => handleDownload(attachment)}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
                <Button
                  kind="danger"
                  small
                  title="Remove attachment"
                  aria-label="Remove attachment"
                  onClick={() => setDeleteTarget(attachment)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContractFormModal open={showEditModal} onClose={() => setShowEditModal(false)} editContract={contract} />
      <Modal
        open={!!preview}
        modalHeading={preview?.name || "Attachment preview"}
        primaryButtonText="Close"
        secondaryButtonText=""
        onRequestClose={closePreview}
        onRequestSubmit={closePreview}
      >
        {preview && (
          <img
            src={preview.url}
            alt={preview.name}
            style={{ display: "block", maxWidth: "100%", maxHeight: "70vh", margin: "0 auto" }}
          />
        )}
      </Modal>
      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDeleteAttachment}
      >
        Are you sure you want to remove &quot;{deleteTarget?.name}&quot;?
      </DeleteConfirmationModal>
    </div>
  )
}
